import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Download, Eye, FileText, Plus, RefreshCw, Search } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import API, { SellOutAPI } from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';
import {
  commitmentStatusColor,
  commitmentStatusLabel,
  downloadCsv,
  fiscalStatusColor,
  fiscalStatusLabel,
  formatDate,
  statusOptions,
  supplierLabel,
} from '../../components/sellout/selloutUtils';

const legacyHeaders = [
  { key: 'supplier', label: 'Proveedor', value: (row) => row?.supplier?.name || '' },
  { key: 'commitment_reference', label: 'Promesa' },
  { key: 'date', label: 'Fecha' },
  { key: 'expected_source_description', label: 'Fuente esperada' },
  { key: 'real_product_code', label: 'Codigo producto real' },
  { key: 'real_product_description', label: 'Producto real' },
  { key: 'accepted_qty', label: 'Cantidad aceptada' },
  { key: 'receive_note_reference', label: 'Receive note' },
  { key: 'promo_invoice', label: 'Factura promo' },
  { key: 'credit_note', label: 'Nota credito' },
  { key: 'status', label: 'Estado' },
];

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

function Metric({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minWidth: 145 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={800}>{value ?? 0}</Typography>
    </Paper>
  );
}

export default function SellOutPromotions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    supplier_id: null,
    status: '',
    date_from: '',
    date_to: '',
    search: '',
  });
  const [snack, setSnack] = useState({
    open: Boolean(location.state?.message),
    severity: 'success',
    message: location.state?.message || '',
  });

  useEffect(() => {
    dispatch(addTab({ label: 'Promesas Sell Out', path: '/sellout/promotions' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    API.get('suppliers/minimal/', { params: { active: true } })
      .then(({ data }) => {
        if (mounted) setSuppliers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mounted) {
          setSnack({ open: true, severity: 'error', message: 'No se pudo cargar proveedores.' });
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === filters.supplier_id) || null,
    [filters.supplier_id, suppliers]
  );

  const buildParams = useCallback(() => {
    const params = {};
    if (filters.supplier_id) params.supplier_id = filters.supplier_id;
    if (filters.status) params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.search.trim()) params.search = filters.search.trim();
    return params;
  }, [filters]);

  const loadCommitments = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [commitmentsRes, dashboardRes] = await Promise.all([
        SellOutAPI.commitments.list(params),
        SellOutAPI.dashboard(params),
      ]);
      setRows(asArray(commitmentsRes.data));
      setDashboard(dashboardRes.data || {});
    } catch {
      setRows([]);
      setDashboard({});
      setSnack({ open: true, severity: 'error', message: 'No se pudo cargar promesas Sell Out.' });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadCommitments();
  }, [loadCommitments]);

  const handleExportLegacy = async () => {
    try {
      const { data } = await SellOutAPI.exportLegacy(buildParams());
      const exportRows = asArray(data);
      if (!exportRows.length) {
        setSnack({ open: true, severity: 'info', message: 'No hay cantidades confirmadas para exportar.' });
        return;
      }
      downloadCsv('sellout-legado.csv', legacyHeaders, exportRows);
    } catch {
      setSnack({ open: true, severity: 'error', message: 'No se pudo exportar legado.' });
    }
  };

  const reviewCount = (row) => (row?.lines || []).filter((line) => line.requires_review).length;

  const columns = [
    {
      field: 'date',
      headerName: 'Fecha',
      width: 115,
      valueGetter: (value) => formatDate(value),
    },
    {
      field: 'supplier',
      headerName: 'Proveedor',
      minWidth: 210,
      flex: 1,
      valueGetter: (value) => supplierLabel(value),
    },
    {
      field: 'reference',
      headerName: 'Referencia',
      minWidth: 140,
      flex: 0.8,
      valueGetter: (value, row) => value || `SO-${row?.id}`,
    },
    {
      field: 'orders',
      headerName: 'Ordenes',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value) => Array.isArray(value) ? value.length : 0,
    },
    {
      field: 'lines',
      headerName: 'Lineas',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value) => Array.isArray(value) ? value.length : 0,
    },
    {
      field: 'promised_total',
      headerName: 'Esperado',
      width: 120,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'received_total',
      headerName: 'Recibida',
      width: 120,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'missing_total',
      headerName: 'Faltante',
      width: 120,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'fiscal_status',
      headerName: 'Fiscal',
      width: 140,
      renderCell: (params) => (
        <Chip size="small" label={fiscalStatusLabel(params.value)} color={fiscalStatusColor(params.value)} />
      ),
    },
    {
      field: 'requires_review',
      headerName: 'Revision',
      width: 120,
      valueGetter: (_, row) => reviewCount(row),
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value > 0 ? params.value : 'OK'}
          color={params.value > 0 ? 'warning' : 'success'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 140,
      renderCell: (params) => (
        <Chip size="small" label={commitmentStatusLabel(params.value)} color={commitmentStatusColor(params.value)} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Ver detalle">
            <IconButton size="small" onClick={() => navigate(`/sellout/promotions/${params.row.id}`)}>
              <Eye size={17} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ingresar documento">
            <IconButton size="small" onClick={() => navigate('/sellout/documents/create', {
              state: {
                selloutDocumentDefaults: {
                  supplier_id: params.row.supplier?.id || null,
                  commitment_id: params.row.id,
                },
              },
            })}>
              <FileText size={17} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Promesas Sell Out</Typography>
          <Typography variant="body2" color="text.secondary">
            Cumplimiento operativo por proveedor y promesa
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshCw size={17} />} onClick={loadCommitments} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="outlined" startIcon={<Download size={17} />} onClick={handleExportLegacy}>
            Exportar legado
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sellout/missing')}>
            Faltantes
          </Button>
          <Button variant="outlined" startIcon={<FileText size={17} />} onClick={() => navigate('/sellout/documents/create')}>
            Ingresar documento
          </Button>
          <Button variant="contained" startIcon={<Plus size={17} />} onClick={() => navigate('/sellout/promotions/create')}>
            Nueva promesa Sell Out
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Metric label="Promesas" value={dashboard.promesas_total ?? dashboard.commitments_total} />
        <Metric label="Lineas" value={dashboard.lineas_total ?? dashboard.lines_total} />
        <Metric label="Esperado" value={dashboard.cantidad_esperada ?? dashboard.expected_qty} />
        <Metric label="Recibido" value={dashboard.cantidad_recibida ?? dashboard.received_qty} />
        <Metric label="Faltante" value={dashboard.cantidad_faltante ?? dashboard.missing_qty} />
        <Metric label="Pendiente NC" value={dashboard.documentos_pendientes_nc ?? dashboard.pending_credit_note_documents} />
        <Metric label="Revision" value={dashboard.lineas_requires_review ?? dashboard.requires_review_lines} />
        <Metric label="Promesas faltante" value={dashboard.promesas_con_faltante ?? dashboard.commitments_with_missing} />
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
          <Autocomplete
            options={suppliers}
            value={selectedSupplier}
            onChange={(_, supplier) => setFilters((prev) => ({ ...prev, supplier_id: supplier?.id || null }))}
            getOptionLabel={supplierLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} size="small" label="Proveedor" />}
            sx={{ minWidth: { md: 280 }, flex: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label="Desde"
            value={filters.date_from}
            onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { md: 150 } }}
          />
          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={filters.date_to}
            onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { md: 150 } }}
          />
          <TextField
            select
            size="small"
            label="Estado"
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            sx={{ minWidth: { md: 180 } }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Buscar"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            InputProps={{ startAdornment: <Search size={17} style={{ marginRight: 8 }} /> }}
            sx={{ minWidth: { md: 260 }, flex: 1 }}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: 560, borderRadius: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f7f8fa' },
          }}
        />
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
